import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Popover,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard,
  Event,
  Assignment,
  AccountCircle,
  Logout,
  AttachMoney as AttachMoneyIcon,
  Close as CloseIcon,
  MoreHoriz,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { authService } from '../../services/authService';
import R1 from '../../images/R1.webp';
import { useAuth } from '../../contexts/AuthContext';
import WelcomePopup from '../common/WelcomePopup';

const drawerWidth = 260;

const ColaboradorLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, logout, refreshUser } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);

  // ── Estado del cambio de contraseña obligatorio (primer acceso) ──
  const [fpData, setFpData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [fpError, setFpError] = useState('');
  const [fpSaving, setFpSaving] = useState(false);
  const [fpShowOld, setFpShowOld] = useState(false);
  const [fpShowNew, setFpShowNew] = useState(false);

  const handleForcedChange = async () => {
    setFpError('');
    if (!fpData.oldPassword) { setFpError('Ingresa tu contraseña temporal'); return; }
    if (fpData.newPassword.length < 6) { setFpError('La nueva contraseña debe tener al menos 6 caracteres'); return; }
    if (fpData.newPassword !== fpData.confirmPassword) { setFpError('Las contraseñas no coinciden'); return; }
    try {
      setFpSaving(true);
      await authService.changePassword(fpData.oldPassword, fpData.newPassword);
      // Actualizar sessionStorage para que el dialog cierre y persista
      const stored = JSON.parse(sessionStorage.getItem('user') || '{}');
      stored.cambiar_password = 0;
      sessionStorage.setItem('user', JSON.stringify(stored));
      refreshUser();
    } catch (err) {
      setFpError(err.response?.data?.message || err.message || 'Error al cambiar la contraseña');
    } finally {
      setFpSaving(false);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/colaborador/dashboard' },
    { text: 'Mis Eventos', icon: <Event />, path: '/colaborador/eventos' },
    { text: 'Mis Tareas', icon: <Assignment />, path: '/colaborador/tareas' },
    { text: 'Mis Finanzas', icon: <AttachMoneyIcon />, path: '/colaborador/finanzas' },
    { text: 'Mi Perfil', icon: <AccountCircle />, path: '/colaborador/perfil' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{
        background: 'linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="h6" noWrap component="div" sx={{ color: '#000', fontWeight: 700 }}>
          A la Vuelta 360°
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#000' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)',
                  color: '#000',
                  '& .MuiListItemIcon-root': {
                    color: '#000',
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const bottomMainItems = menuItems.slice(0, 3);
  const bottomExtraItems = menuItems.slice(3);
  const currentBottomNavValue = bottomMainItems.findIndex((item) => location.pathname === item.path);
  const isExtraActive = bottomExtraItems.some((item) => location.pathname === item.path);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: '#131829',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate('/colaborador/dashboard')}
            sx={{
              mr: 1,
              display: { sm: 'none' },
              p: 0,
              width: 38,
              height: 38,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px solid rgba(255,255,255,0.15)',
              '&:hover': { border: '2px solid rgba(255,255,255,0.4)' },
            }}
          >
            <Box
              component="img"
              src={R1}
              alt="Inicio"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, textAlign: isMobile ? "center" : "left" }}>
            {isMobile
              ? (user?.colaborador?.nombre_completo || user?.nombre_completo || user?.username || 'Mi Panel')
              : 'Panel de Colaborador'}
          </Typography>
          <IconButton onClick={handleMenuOpen} color="inherit">
            <Avatar src={user?.colaborador?.foto_url || undefined} sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: '#000' }}>
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/colaborador/perfil'); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>{user?.username}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          mt: 8,
          pb: { xs: 9, sm: 3 },
        }}
      >
        <Outlet />
        <WelcomePopup
          userRol={user?.rol}
          userName={user?.colaborador?.nombre_completo || user?.username}
          cambiarPassword={Number(user?.cambiar_password) === 1}
        />
      </Box>

      {/* ── Dialog: cambio de contraseña obligatorio (primer acceso) ── */}
      <Dialog
        open={Number(user?.cambiar_password) === 1}
        disableEscapeKeyDown
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)',
            border: '1px solid rgba(255,255,255,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#fff', fontWeight: 800, pb: 0 }}>
          <LockIcon sx={{ color: '#00C9FF', fontSize: 28 }} />
          Cambia tu contraseña para continuar
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
          <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
            Por seguridad debes cambiar tu contraseña temporal antes de usar el portal.
            La contraseña temporal fue enviada a tu correo electrónico al ser aprobado.
          </Alert>
          {fpError && <Alert severity="error">{fpError}</Alert>}
          <TextField
            label="Contraseña temporal (actual)"
            type={fpShowOld ? 'text' : 'password'}
            value={fpData.oldPassword}
            onChange={(e) => setFpData(p => ({ ...p, oldPassword: e.target.value }))}
            fullWidth
            autoFocus
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={() => setFpShowOld(v => !v)} edge="end">
                  {fpShowOld ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              ),
            }}
          />
          <TextField
            label="Nueva contraseña"
            type={fpShowNew ? 'text' : 'password'}
            value={fpData.newPassword}
            onChange={(e) => setFpData(p => ({ ...p, newPassword: e.target.value }))}
            fullWidth
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={() => setFpShowNew(v => !v)} edge="end">
                  {fpShowNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              ),
            }}
          />
          <TextField
            label="Confirmar nueva contraseña"
            type="password"
            value={fpData.confirmPassword}
            onChange={(e) => setFpData(p => ({ ...p, confirmPassword: e.target.value }))}
            fullWidth
            onKeyDown={(e) => e.key === 'Enter' && !fpSaving && handleForcedChange()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleForcedChange}
            disabled={fpSaving}
            startIcon={fpSaving ? <CircularProgress size={18} sx={{ color: '#000' }} /> : <LockIcon />}
            sx={{
              background: 'linear-gradient(90deg, #00C9FF 0%, #92FE9D 100%)',
              color: '#000',
              fontWeight: 800,
              borderRadius: '50px',
              py: 1.4,
            }}
          >
            {fpSaving ? 'Actualizando...' : 'Cambiar contraseña y continuar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bottom Navigation (solo móvil) ── */}
      <Paper
        sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={isExtraActive ? 3 : currentBottomNavValue !== -1 ? currentBottomNavValue : false}
          onChange={(e, newValue) => {
            if (newValue < 3) navigate(bottomMainItems[newValue].path);
          }}
          sx={{
            bgcolor: '#131829',
            '& .MuiBottomNavigationAction-root': { color: 'rgba(255,255,255,0.5)' },
            '& .MuiBottomNavigationAction-root.Mui-selected': { color: '#00C9FF' },
            '& .MuiBottomNavigationAction-label': { fontSize: '0.65rem' },
          }}
        >
          {bottomMainItems.map((item) => (
            <BottomNavigationAction key={item.text} label={item.text} icon={item.icon} />
          ))}
          <BottomNavigationAction
            label="Más"
            icon={<MoreHoriz />}
            onClick={(e) => setMoreAnchorEl(e.currentTarget)}
          />
        </BottomNavigation>
        <Popover
          open={Boolean(moreAnchorEl)}
          anchorEl={moreAnchorEl}
          onClose={() => setMoreAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: {
              bgcolor: '#1a2035',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 2,
              minWidth: 200,
            },
          }}
        >
          {bottomExtraItems.map((item) => (
            <MenuItem
              key={item.text}
              onClick={() => { navigate(item.path); setMoreAnchorEl(null); }}
              sx={{
                color: location.pathname === item.path ? '#00C9FF' : 'rgba(255,255,255,0.85)',
                py: 1.5,
                '& .MuiListItemIcon-root': {
                  color: location.pathname === item.path ? '#00C9FF' : 'rgba(255,255,255,0.5)',
                  minWidth: 40,
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.text}</ListItemText>
            </MenuItem>
          ))}
        </Popover>
      </Paper>
    </Box>
  );
};

export default ColaboradorLayout;
